import React, { useMemo } from "react";
import {
  Box,
  Group,
  Menu,
  Popover,
  Button,
  ActionIcon,
  Checkbox,
  ScrollArea,
  Stack,
  Text,
  TagsInput,
  MultiSelect,
  Select,
  Loader,
} from "@mantine/core";
import {
  IconColumns,
  IconDeviceFloppy,
  IconDots,
  IconFileSpreadsheet,
  IconFileTypeCsv,
  IconFilter,
  IconRotate,
  IconTrash,
} from "@tabler/icons-react";
import {
  DateRangePreset,
  ReportDefinition,
  ReportFilter,
} from "../../types/reports";
import DateRangeFilter from "./DateRangeFilter";
import { useSalon } from "../../context/SalonContext";
import { useProducts } from "../../context/ProductsContext";
import { Employee, SalonService } from "../../types/salon";
import { Product } from "../../types/products";
import styles from "./ReportsPage.module.css";

interface Props {
  definition: ReportDefinition;
  preset: DateRangePreset;
  customStart: string | null;
  customEnd: string | null;
  onDateRangeChange: (
    preset: DateRangePreset,
    customStart: string | null,
    customEnd: string | null,
  ) => void;
  hiddenColumns: string[];
  onToggleColumn: (key: string) => void;
  filterValues: Record<string, string[]>;
  onFilterChange: (key: string, value: string[]) => void;
  isDirty: boolean;
  isSaving: boolean;
  isLoading?: boolean;
  isDeleting?: boolean;
  onSaveView: () => void;
  onReset: () => void;
  onDownloadCsv: () => void;
  onDownloadExcel: () => void;
  onDelete: () => void;
}

export default function ReportToolbar({
  definition,
  preset,
  customStart,
  customEnd,
  onDateRangeChange,
  hiddenColumns,
  onToggleColumn,
  filterValues,
  onFilterChange,
  isDirty,
  isSaving,
  isLoading = false,
  isDeleting = false,
  onSaveView,
  onReset,
  onDownloadCsv,
  onDownloadExcel,
  onDelete,
}: Props) {
  // While the report is fetching, lock down every interactive control so
  // users don't fire a second run mid-flight or trigger a download against
  // stale data.
  const disabled = isLoading || isSaving || isDeleting;

  // Pull the rosters used to translate filter IDs into human-readable
  // names. The reports microservice still filters by ID, but the toolbar
  // shows staff/service/product names so users aren't forced to memorize
  // UUIDs.
  const { employees, services } = useSalon() as unknown as {
    employees: Employee[] | null;
    services: SalonService[] | null;
  };
  const { products } = useProducts();

  const staffOptions = useMemo(
    () =>
      (employees || []).map((e) => ({
        value: e.id,
        label: `${e.first_name} ${e.last_name}`.trim() || e.email || e.id,
      })),
    [employees],
  );
  const serviceOptions = useMemo(
    () =>
      (services || []).map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    [services],
  );
  const productOptions = useMemo(
    () =>
      (products || []).map((p: Product) => ({
        value: p.id,
        label: p.name,
      })),
    [products],
  );

  const renderFilter = (f: ReportFilter) => {
    const value = filterValues[f.key] || [];
    const onChange = (next: string[]) => onFilterChange(f.key, next);
    switch (f.kind) {
      case "staff_multiselect":
        return (
          <MultiSelect
            key={f.key}
            label={f.label}
            placeholder={value.length === 0 ? "All staff" : ""}
            data={staffOptions}
            value={value}
            onChange={onChange}
            searchable
            clearable
            nothingFoundMessage="No staff found"
          />
        );
      case "service_multiselect":
        return (
          <MultiSelect
            key={f.key}
            label={f.label}
            placeholder={value.length === 0 ? "All services" : ""}
            data={serviceOptions}
            value={value}
            onChange={onChange}
            searchable
            clearable
            nothingFoundMessage="No services found"
          />
        );
      case "product_multiselect":
        return (
          <MultiSelect
            key={f.key}
            label={f.label}
            placeholder={value.length === 0 ? "All products" : ""}
            data={productOptions}
            value={value}
            onChange={onChange}
            searchable
            clearable
            nothingFoundMessage="No products found"
          />
        );
      case "enum_multiselect":
        // The backend ships value/label pairs for static enums
        // (transaction status, appointment status) so the frontend
        // doesn't have to mirror the enum values. We just hand
        // Mantine the data array as-is.
        return (
          <MultiSelect
            key={f.key}
            label={f.label}
            placeholder={
              value.length === 0 ? `All ${f.label.toLowerCase()}` : ""
            }
            data={f.options ?? []}
            value={value}
            onChange={onChange}
            searchable
            clearable
            nothingFoundMessage="No options"
          />
        );
      case "enum_select": {
        // Single choice from backend-supplied options (e.g. which date the
        // range applies to). The first option is the backend default, so an
        // unset filter displays it and picking it clears the value — saved
        // views stay minimal and isDirty only flips on a real change.
        const options = f.options ?? [];
        const defaultValue = options[0]?.value ?? null;
        return (
          <Select
            key={f.key}
            label={f.label}
            data={options}
            value={value[0] ?? defaultValue}
            onChange={(next) =>
              onChange(next && next !== defaultValue ? [next] : [])
            }
            allowDeselect={false}
          />
        );
      }
      case "service_or_product_multiselect":
        // One MultiSelect that mixes services and products. We
        // prefix the values so the backend can route each id into
        // the right SQL param, and we group the dropdown so the
        // user can tell at a glance whether a row is a service or a
        // product when names overlap.
        return (
          <MultiSelect
            key={f.key}
            label={f.label}
            placeholder={value.length === 0 ? "All services and products" : ""}
            data={[
              {
                group: "Services",
                items: serviceOptions.map((o) => ({
                  value: `service:${o.value}`,
                  label: o.label,
                })),
              },
              {
                group: "Products",
                items: productOptions.map((o) => ({
                  value: `product:${o.value}`,
                  label: o.label,
                })),
              },
            ]}
            value={value}
            onChange={onChange}
            searchable
            clearable
            nothingFoundMessage="No services or products found"
          />
        );
      default:
        return (
          <TagsInput
            key={f.key}
            label={f.label}
            placeholder="Add a value"
            value={value}
            onChange={onChange}
          />
        );
    }
  };
  return (
    <Box className={styles.toolbar}>
      <DateRangeFilter
        preset={preset}
        customStart={customStart}
        customEnd={customEnd}
        onChange={onDateRangeChange}
        disabled={disabled}
      />

      <Menu position="bottom-start" withinPortal closeOnItemClick={false}>
        <Menu.Target>
          <Button
            leftSection={<IconColumns size={14} />}
            variant="default"
            disabled={disabled}
          >
            Columns
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Stack gap={4} p="xs">
            <Text size="xs" c="dimmed">
              Show / hide columns
            </Text>
            {/* Cap the checkbox list at a viewport-relative height so a
                long, dynamic column set (e.g. Transaction Log's per-payment
                -method breakdown columns) scrolls inside the dropdown instead
                of overflowing off-screen. dvh (not vh) keeps it fully visible
                under mobile browser chrome. */}
            <ScrollArea.Autosize mah="min(360px, 60dvh)" type="auto">
              <Stack gap={4} pr="xs">
                {definition.columns.map((c) => (
                  <Checkbox
                    key={c.key}
                    label={c.label}
                    checked={!hiddenColumns.includes(c.key)}
                    onChange={() => onToggleColumn(c.key)}
                  />
                ))}
              </Stack>
            </ScrollArea.Autosize>
          </Stack>
        </Menu.Dropdown>
      </Menu>

      {definition.filters.length > 0 && (
        // Popover (not Menu) so the embedded MultiSelect comboboxes
        // can open without their portaled dropdowns being treated as
        // an outside click that collapses the parent.
        <Popover position="bottom-start" withinPortal shadow="md">
          <Popover.Target>
            <Button
              leftSection={<IconFilter size={14} />}
              variant="default"
              disabled={disabled}
            >
              Filters
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs" w={320}>
              <Text size="xs" c="dimmed">
                Restrict this report to specific staff, services, or products.
                Leave blank to include everything.
              </Text>
              {definition.filters.map(renderFilter)}
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}

      {/* Save View / Reset only surface when the on-screen state
          differs from the saved defaults. Both are subtle so they
          live alongside the regular toolbar without competing for
          attention. */}
      {isDirty && (
        <Group gap={4}>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={onSaveView}
            loading={isSaving}
            disabled={disabled}
          >
            Save View
          </Button>
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconRotate size={14} />}
            onClick={onReset}
            disabled={disabled}
          >
            Reset
          </Button>
        </Group>
      )}

      <Box className={styles.toolbarSpacer} />

      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Report menu"
            disabled={disabled}
          >
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Download</Menu.Label>
          <Menu.Item
            leftSection={<IconFileTypeCsv size={14} />}
            onClick={onDownloadCsv}
            disabled={disabled}
          >
            Download as CSV
          </Menu.Item>
          <Menu.Item
            leftSection={<IconFileSpreadsheet size={14} />}
            onClick={onDownloadExcel}
            disabled={disabled}
          >
            Download as Excel
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            color="red"
            leftSection={
              isDeleting ? (
                <Loader size="xs" color="red" />
              ) : (
                <IconTrash size={14} />
              )
            }
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Removing..." : "Remove from my reports"}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
